import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

import { BULK_WARNING_KEYS, bulkOutcomeTone, type BulkOutcome } from './bulkActions';

/**
 * Typed confirm for destructive bulk actions (wave 3 #6-UI): shutting down or
 * restarting a wall of machines, unlocking support mode, pausing a whole row.
 * The employee types the NUMBER of affected devices — digits are the one
 * spelling shared by the app's three languages, and typing "12" proves the
 * hand knows what the eye selected.
 *
 * After the run, every device's own answer is listed — the runner never stops
 * at a failure, and this dialog never summarises one away.
 */
export function BulkConfirmDialog({
  title,
  message,
  actionLabel,
  deviceNames,
  typedConfirm = true,
  children,
  autoFocusTypedCount = true,
  run,
  onClose,
  onFinished,
}: {
  title: string;
  message: string;
  actionLabel: string;
  deviceNames: readonly string[];
  /**
   * Make the employee type the count before the action arms. On by default,
   * because the actions this dialog was built for take a wall of machines away
   * from paying customers.
   *
   * Off for the ones that only START something. Seating a group is the busiest
   * thing that happens at the counter, and a number to type before customers
   * can sit down is friction with nothing to protect: the worst case is a
   * session staff end again a moment later. The device list and the per-device
   * outcomes below still apply — those are what make the action honest.
   */
  typedConfirm?: boolean;
  /**
   * What the action must still ask before it runs, shown under the device
   * names while confirming — Outstanding's End and the bulk Pause ask a
   * round-priced device's rounds here (OW19). `run` reads whatever was
   * answered.
   */
  children?: ReactNode;
  /**
   * The typed count takes the cursor when the dialog opens. Off when
   * `children` put it in a field of their own — the rounds open in Rounds, as
   * the Device List's End does — because the count, a later field, would
   * otherwise take it back.
   */
  autoFocusTypedCount?: boolean;
  run: () => Promise<BulkOutcome[]>;
  onClose: () => void;
  /** Called once the run settles (any mix of outcomes) so the board reloads. */
  onFinished: () => void;
}) {
  const { t } = useTranslation();
  const count = deviceNames.length;

  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState<'confirm' | 'running' | 'done'>('confirm');
  const [outcomes, setOutcomes] = useState<BulkOutcome[]>([]);

  const armed = !typedConfirm || typed.trim() === String(count);

  async function handleRun(): Promise<void> {
    setPhase('running');
    const results = await run();
    setOutcomes(results);
    setPhase('done');
    onFinished();
  }

  const failedCount = outcomes.filter((outcome) => !outcome.ok).length;
  const warnedCount = outcomes.filter((outcome) => bulkOutcomeTone(outcome) === 'warning').length;

  function failureText(outcome: BulkOutcome): string {
    if (outcome.code === 'no_session') return t('deviceBoard.bulk.noSession');
    return outcome.message ?? t('deviceBoard.bulk.failedGeneric');
  }

  /**
   * The half that did not land, for a device whose action SUCCEEDED. Kept out
   * of `failureText` on purpose: this row is not a failure and must never read
   * as one, or staff learn to distrust the failure count.
   */
  function detailText(outcome: BulkOutcome): string {
    if (bulkOutcomeTone(outcome) === 'warning' && outcome.warning) {
      return t(BULK_WARNING_KEYS[outcome.warning]);
    }
    return failureText(outcome);
  }

  const summaryText =
    failedCount > 0
      ? t('deviceBoard.bulk.someFailed', {
          ok: outcomes.length - failedCount,
          failed: failedCount,
        })
      : warnedCount > 0
        ? t('deviceBoard.bulk.allOkSomeWarned', { count: outcomes.length, warned: warnedCount })
        : t('deviceBoard.bulk.allOk', { count: outcomes.length });

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="flex max-h-full w-full max-w-md flex-col rounded-card border border-subtle bg-raised p-5 shadow-2xl">
        <h2 className="text-lg font-semibold text-content">{title}</h2>

        {phase === 'confirm' ? (
          <>
            <p className="mt-2 text-sm text-content-secondary">{message}</p>
            <p className="mt-2 max-h-24 overflow-auto text-xs text-content-muted">
              {deviceNames.join(' · ')}
            </p>

            {children}

            {typedConfirm ? (
              <label className="mt-4 block">
                <span className="mb-1 block text-xs text-content-secondary">
                  {t('deviceBoard.bulk.typeToConfirm', { count })}
                </span>
                <input
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  inputMode="numeric"
                  autoFocus={autoFocusTypedCount}
                  className="tabular w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none focus:border-primary"
                />
              </label>
            ) : null}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleRun()}
                disabled={!armed}
                autoFocus={!typedConfirm}
                className={[
                  'rounded-full px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
                  typedConfirm
                    ? 'bg-danger text-content-inverse hover:bg-danger-hover'
                    : 'bg-primary text-primary-fg hover:bg-primary-hover',
                ].join(' ')}
              >
                {actionLabel}
              </button>
            </div>
          </>
        ) : phase === 'running' ? (
          <p className="mt-3 text-sm text-content-muted">{t('deviceBoard.bulk.running')}</p>
        ) : (
          <>
            <p className="mt-2 text-sm text-content-secondary">{summaryText}</p>

            <ul className="mt-3 min-h-0 flex-1 space-y-1 overflow-auto">
              {outcomes.map((outcome) => {
                const tone = bulkOutcomeTone(outcome);
                return (
                  <li
                    key={outcome.deviceId}
                    data-bulk-outcome={tone}
                    className="flex items-start gap-2 rounded-control bg-input px-2 py-1.5 text-xs"
                  >
                    {tone === 'ok' ? (
                      <CheckCircle2
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-available"
                        aria-hidden
                      />
                    ) : tone === 'warning' ? (
                      // Amber, not red: the action worked. Only the half it
                      // could not reach is being reported here.
                      <AlertTriangle
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ending"
                        aria-hidden
                      />
                    ) : (
                      <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger" aria-hidden />
                    )}
                    <span className="min-w-0">
                      <span className="font-medium text-content">{outcome.deviceName}</span>
                      {tone === 'ok' ? null : (
                        <span
                          className={`block ${tone === 'warning' ? 'text-ending' : 'text-content-muted'}`}
                        >
                          {detailText(outcome)}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover"
              >
                {t('deviceBoard.bulk.done')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
